// Phase 6: optional localhost bridge to the Windows Customer Display Helper
// (customer-display-helper/lib/RatingBridge.psm1 + RatingKeyHook.psm1), so a
// physical RP2040 rating remote (F13/F14/F15) can drive the existing
// RatingScreen.vue as if it had been tapped. Framework-independent and pure
// except for the injected WebSocket constructor, so it is unit-testable
// without a real socket -- same shape as transport.js's createTransport(win).
//
// This module carries NO rating/session/document data of its own: it only
// ever sends/receives an opaque requestId, a rating (1/2/3), a random
// pageInstanceId minted once per BRIDGE OBJECT (never a real session id --
// see below), an incrementing per-opportunity generation counter owned by
// the caller (see CustomerDisplayPage.vue), and one of the fixed OUTCOME
// strings from submitRating.js (plus 'unavailable', for when there is no
// ratable session to apply an intent to). The actual rating submission
// stays entirely in submitRating.js/useSubmitRating.js, called by whoever
// wires this bridge's onIntent() callback (see RatingScreen.vue /
// CustomerDisplayPage.vue) -- this module never calls it directly.
//
// pageInstanceId is generated fresh, in memory only, every time
// createHelperBridge() is called (i.e. once per page load) -- and
// DELIBERATELY NEVER PERSISTED anywhere (not sessionStorage, not
// localStorage): it must survive a WebSocket reconnect of THIS SAME bridge
// object (a dropped connection reconnecting via scheduleReconnect()/
// connect() below does not recreate the bridge, so pageInstanceId stays
// identical across it -- this is what lets the Helper's PAID dedup survive
// a reconnect instead of re-arming and sounding twice), but a genuine page
// reload always calls createHelperBridge() again from scratch and must get
// a BRAND NEW, unrelated pageInstanceId (an EARLIER revision persisted it
// across a reload specifically to "help" this, but that let a reload's
// fresh page collide with whatever state the pre-reload page had already
// reached under the SAME identifier -- see
// RatingControllerProtocol.psm1's header for the full history/reasoning of
// why persistence was removed while the per-bridge identity stays).
// pageInstanceId is paired with the caller's own Generation counter for
// this: a different pageInstanceId always supersedes the one before it,
// and Generation orders messages WITHIN one pageInstanceId's lifetime.
//
// This is entirely separate from -- and never confused with -- the
// ConnectionSequence ordering identity the HELPER itself assigns per
// WebSocket connection it accepts (RatingNative.psm1's RatingBridgeServer);
// this module never sees or sends that value at all, since it exists
// purely as a native-side defense against a DIFFERENT race (a delayed
// PowerShell event from an already-superseded connection) -- see
// RatingControllerProtocol.psm1's header for the full picture of how the
// two identities divide the work.
//
// On a machine with no Helper running (the normal case for a plain browser,
// a dev machine, or a Customer Display with no hardware remote attached),
// the WebSocket connection simply fails to open and this module keeps
// quietly retrying in the background with a bounded exponential backoff
// (below) -- never a thrown error, never console spam beyond the browser's
// own connection-failed log.

const RATING_VALUES = [1, 2, 3];
const PAGE_INSTANCE_ID_BYTES = 16;

// Fresh, random, in-memory-only -- never persisted (see this file's header
// on why). Falls back gracefully if crypto is unavailable (a very old or
// locked-down browser): correctness only needs this to be different across
// separate bridge instances with overwhelming probability, not
// cryptographically unguessable.
function createPageInstanceId(w) {
  const browserCrypto = w && w.crypto ? w.crypto : (typeof crypto !== 'undefined' ? crypto : null);
  try {
    if (browserCrypto && typeof browserCrypto.randomUUID === 'function') {
      return browserCrypto.randomUUID();
    }
    if (browserCrypto && typeof browserCrypto.getRandomValues === 'function') {
      const bytes = browserCrypto.getRandomValues(new Uint8Array(PAGE_INSTANCE_ID_BYTES));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (error) {
    // fall through to the Math.random() fallback below
  }
  let id = '';
  for (let i = 0; i < PAGE_INSTANCE_ID_BYTES * 2; i += 1) {
    id += Math.floor(Math.random() * 16).toString(16);
  }
  return id;
}

// Mirrors submitRating.js's OUTCOME values exactly, plus 'unavailable' for
// "the display had nothing ratable to apply this intent to" (e.g. the
// screen was not showing the rating prompt, or a submission was already in
// flight). Kept here too (not just imported) so this module has no runtime
// dependency on submitRating.js -- callers translate their own outcome into
// one of these strings.
export const HELPER_OUTCOMES = ['success', 'already-rated', 'expired', 'invalid', 'network-error', 'unavailable'];

// RP2040 rating-controller protocol (READY/OFF/PAID/ACK/RETRY LEDs+sound,
// see customer-display-helper/lib/RatingControllerProtocol.psm1): three new,
// one-directional, browser -> Helper broadcast message types, additive to
// the rating-intent/rating-result pair above and carried over the exact
// same bridge connection -- the hardware remote's own request/reply flow is
// completely unchanged. Also carries no session/document data: only a fixed
// state/outcome string, the caller's own generation counter, and for an
// outcome, a rating (1/2/3).
export const RATING_CONTROLLER_STATES = ['ready', 'unavailable'];
// Deliberately excludes 'unavailable' (that value is specific to
// HELPER_OUTCOMES/rating-result above, meaning "no session to apply a
// hardware intent to" -- a different concept from a real submission
// outcome, which is all this broadcast ever reports).
export const RATING_CONTROLLER_OUTCOMES = ['success', 'already-rated', 'expired', 'invalid', 'network-error'];

// Bounded exponential backoff for reconnecting to the Helper -- requirement:
// "WebSocket state replay on open and reconnect with bounded backoff".
// Doubles each attempt, capped, and never gives up on its own (a kiosk
// display stays open for a whole shift; the Helper may start after the
// browser, or restart independently) -- only close() stops it.
const RECONNECT_BASE_DELAY_MS = 500;
const RECONNECT_MAX_DELAY_MS = 30000;

// How many recently-seen rating-intent requestIds this module remembers, to
// ignore an exact duplicate delivery of the same intent (requirement: never
// let a delayed duplicate intent cause a second submit). Small and bounded
// -- this is defense against an unexpected duplicate frame, not a real
// history; legitimate distinct presses always get fresh GUIDs from the
// Helper's native TrySendRatingIntent.
const MAX_SEEN_REQUEST_IDS = 20;

function str(value, max) {
  if (value === null || value === undefined) return '';
  return String(value).trim().slice(0, max);
}

// Reads helperPort/helperToken from the URL fragment the Helper launched
// this page with (see customer-display-helper's ChromeLauncher.psm1 /
// RatingBridgeProtocol.psm1's Add-RatingBridgeFragment). A fragment, never a
// query string: it is never sent to any server and never appears in a
// Referer header. Returns null when either piece is missing -- the normal
// case without the Helper, handled by createHelperBridge() as "disabled".
export function parseHelperBridgeLocation(hash) {
  const raw = str(hash, 512).replace(/^#/, '');
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const port = Number(params.get('helperPort'));
  const token = str(params.get('helperToken'), 256);
  if (!Number.isInteger(port) || port <= 0 || port > 65535 || !token) return null;
  return { port, token };
}

// Validates an inbound 'rating-intent' message; returns a normalized copy or
// null for anything malformed/unexpected -- mirrors protocol.js's own
// sanitize-on-the-way-in philosophy (never trust the wire).
export function parseRatingIntentMessage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.type !== 'rating-intent') return null;
  const requestId = str(raw.requestId, 64);
  if (!requestId) return null;
  const rating = Number(raw.rating);
  if (RATING_VALUES.indexOf(rating) === -1) return null;
  return { requestId, rating };
}

export function createHelperBridge(win) {
  const w = win || (typeof window !== 'undefined' ? window : null);
  const location = parseHelperBridgeLocation(w && w.location ? w.location.hash : '');
  const listeners = [];
  const emit = (message) => listeners.slice().forEach((fn) => fn(message));
  const pageInstanceId = createPageInstanceId(w);

  if (!location || !w || typeof w.WebSocket !== 'function') {
    // No Helper on this machine (or no WebSocket support) -- a fully silent
    // no-op, never a thrown error: this page must work identically with or
    // without the hardware remote. pageInstanceId is still exposed (even
    // though nothing is ever sent) so a caller can read it unconditionally.
    return {
      enabled: false,
      pageInstanceId,
      onIntent() {
        return () => {};
      },
      sendResult() {},
      sendState() {},
      sendPaid() {},
      sendOutcome() {},
      close() {}
    };
  }

  let socket = null;
  let closed = false;
  let reconnectAttempt = 0;
  let reconnectTimer = null;
  const seenRequestIds = [];

  // The latest state this page told the Helper (requirement: replay the
  // latest authoritative state after reconnect) -- outcome is deliberately
  // never remembered/replayed here, same as the Helper's own
  // steady-state-only replay rule (RatingControllerSerial.psm1): it is a
  // one-shot event, not a standing picture to resync to.
  let lastState = null; // { state, generation } | null

  // A PAID call that has not yet been CONFIRMED delivered -- cleared only
  // once the matching 'rating-paid-ack' comes back from the Helper (see
  // onmessage below), never merely because socket.send() did not throw.
  // socket.send() returning without throwing only means the browser handed
  // the bytes to the OS for this connection; it is not a delivery
  // confirmation -- if the WebSocket connection drops right after send()
  // returns but before the Helper actually processes the frame, nothing
  // else would ever tell this page the PAID was lost (bug fix: the
  // previous version cleared pendingPaid as soon as send() merely
  // succeeded, so this exact race -- disconnect after send(), before the
  // Helper processes it -- silently dropped the one-shot PAID signal
  // forever). Resent on every reconnect while still pending -- safe even if
  // the opportunity has since moved on, since the Helper's own
  // exact-(ConnectionSequence,generation)-match check for Paid events (see
  // RatingControllerProtocol.psm1) silently ignores a no-longer-current
  // retry; it still acknowledges receiving it either way, which is what
  // actually clears this.
  let pendingPaid = null; // { type, generation } | null

  function sendRaw(payload) {
    if (!socket || socket.readyState !== w.WebSocket.OPEN) return false;
    try {
      socket.send(JSON.stringify(payload));
      return true;
    } catch (error) {
      return false;
    }
  }

  function scheduleReconnect() {
    if (closed) return;
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttempt), RECONNECT_MAX_DELAY_MS);
    reconnectAttempt += 1;
    reconnectTimer = w.setTimeout(connect, delay); // eslint-disable-line no-use-before-define
  }

  function connect() {
    if (closed) return;
    try {
      socket = new w.WebSocket(`ws://127.0.0.1:${location.port}/?token=${encodeURIComponent(location.token)}`);
    } catch (error) {
      scheduleReconnect();
      return;
    }
    socket.onopen = () => {
      reconnectAttempt = 0;
      // Requirement: replay the latest authoritative state after reconnect
      // (covers both the very first open and every later reconnect alike).
      if (lastState) sendRaw({ type: 'rating-state', state: lastState.state, pageInstanceId, generation: lastState.generation });
      // Retry a PAID that has not yet been acknowledged -- see pendingPaid's
      // declaration above for why this must be retried on every reconnect
      // (not just once on a failed send) until the Helper actually confirms
      // receipt.
      if (pendingPaid) sendRaw(pendingPaid);
    };
    socket.onmessage = (event) => {
      let raw;
      try {
        raw = JSON.parse(event.data);
      } catch (error) {
        return; // Malformed frame: ignored, never thrown into the page.
      }
      if (raw && raw.type === 'rating-paid-ack') {
        // Delivery confirmed -- see pendingPaid's declaration above. Only
        // clears the CURRENTLY pending one, matched by generation (never by
        // socket.send() merely not throwing).
        if (pendingPaid && raw.generation === pendingPaid.generation) pendingPaid = null;
        return;
      }
      const intent = parseRatingIntentMessage(raw);
      if (!intent) return;
      // Requirement: ignore a delayed duplicate delivery of the same
      // requestId rather than submitting a second time for one physical
      // press.
      if (seenRequestIds.indexOf(intent.requestId) !== -1) return;
      seenRequestIds.push(intent.requestId);
      if (seenRequestIds.length > MAX_SEEN_REQUEST_IDS) seenRequestIds.shift();
      emit(intent);
    };
    socket.onclose = () => scheduleReconnect();
    // onerror is intentionally not handled separately: per standard
    // WebSocket behavior a failed/dropped connection always also fires
    // onclose, which is the single place reconnection is scheduled from --
    // handling both would risk scheduling it twice for one failure.
  }

  connect();

  return {
    enabled: true,
    // Random and fixed for the lifetime of this bridge object (i.e. this
    // page load), including every WebSocket reconnect -- never a real
    // session id (see this file's header). Exposed so a caller can read it
    // if ever needed, though sendState/sendPaid/sendOutcome already attach
    // it to every payload automatically; callers only ever pass generation.
    pageInstanceId,
    onIntent(fn) {
      listeners.push(fn);
      return () => listeners.splice(listeners.indexOf(fn), 1);
    },
    // Sent only after the real submitRating/useSubmitRating flow this
    // intent triggered has actually resolved (or been judged unavailable) --
    // never speculatively, never before.
    sendResult(requestId, outcome) {
      if (HELPER_OUTCOMES.indexOf(outcome) === -1) return;
      sendRaw({ type: 'rating-result', requestId: str(requestId, 64), outcome });
    },
    // Whether rating is currently being offered to the customer at all
    // (full-screen or inline) -- drives the RP2040's READY/OFF LEDs.
    // Never tied to a specific requestId: this is a standing state
    // broadcast, not a reply, and is remembered for replay on reconnect
    // regardless of whether this particular send succeeds right now.
    // `generation` is this page's own small per-opportunity counter,
    // paired with this bridge's own pageInstanceId (see this file's
    // header on why that pairing, not either one alone).
    sendState(state, generation) {
      if (RATING_CONTROLLER_STATES.indexOf(state) === -1) return;
      lastState = { state, generation };
      sendRaw({ type: 'rating-state', state, pageInstanceId, generation });
    },
    // Payment completed, customer has not rated yet -- drives the RP2040's
    // PAID sound reminder. The Helper's own dedup (keyed on the exact
    // (pageInstanceId, generation) pair, which stays identical across a
    // mere reconnect of this SAME bridge) makes sure this is only ever
    // acted on once per such transition even if this gets called more than
    // once, or retried after a reconnect. Remembered as pending and
    // retried on every reconnect until the Helper's own 'rating-paid-ack'
    // confirms delivery -- see pendingPaid's declaration above.
    sendPaid(generation) {
      const payload = { type: 'rating-paid', pageInstanceId, generation };
      pendingPaid = payload;
      sendRaw(payload);
    },
    // The real result of a rating SUBMISSION -- regardless of whether it was
    // triggered by a tap or by the hardware remote -- drives ACK/RETRY/OFF
    // (never send an ACK merely because a rating key was pressed; only
    // this, following the real backend result, does). `rating` is omitted
    // unless it's a definite 1/2/3. `generation` (paired with this
    // bridge's own pageInstanceId) lets the Helper reject a stale outcome
    // that belongs to an opportunity that is no longer current (e.g. a
    // late network response for a customer who has since walked away and a
    // new one started).
    sendOutcome(outcome, rating, generation) {
      if (RATING_CONTROLLER_OUTCOMES.indexOf(outcome) === -1) return;
      const payload = { type: 'rating-outcome', outcome, pageInstanceId, generation };
      if (rating === 1 || rating === 2 || rating === 3) payload.rating = rating;
      sendRaw(payload);
    },
    close: () => {
      closed = true;
      if (reconnectTimer) w.clearTimeout(reconnectTimer);
      reconnectTimer = null;
      if (socket) socket.close();
    }
  };
}
